import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { proXpApi } from "@/lib/experienceApi";
import { formatParisDate } from "@/lib/dtParis";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Building2, Calendar, Plus, ArrowRight, CheckCircle2, AlertCircle,
  MapPin, Rocket,
} from "lucide-react";
import { auth } from "@/lib/api";

export default function ProExperienceHome() {
  const [orgs, setOrgs] = useState([]);
  const [events, setEvents] = useState([]); // {ev, org}
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    if (!auth.getToken()) { nav("/login?next=/pro/experience"); return; }
    const os = await proXpApi.listOrgs().catch(() => []);
    setOrgs(os);
    // Fan-out: fetch events per org in parallel
    const all = await Promise.all(os.map(async (o) => {
      const evs = await proXpApi.listEvents(o.id).catch(() => []);
      return evs.map((e) => ({ ev: e, org: o }));
    }));
    const flat = all.flat().sort((a, b) => new Date(b.ev.starts_at) - new Date(a.ev.starts_at));
    setEvents(flat);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const createOrg = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const org = await proXpApi.createOrg({ name: name.trim(), country: "FR", currency: "EUR" });
      toast.success("Organisation créée");
      setOrgs([org, ...orgs]); setShowNew(false); setName("");
      nav(`/pro/experience/orgs/${org.id}`);
    } catch (e) { toast.error("Impossible"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-display text-[#1F3A5F]">geoVINUM Experience</h1>
          <p className="text-sm text-neutral-600">Créez vos événements de dégustation, vendez vos billets, animez le jour J.</p>
        </div>
        <Button data-testid="new-org-btn" onClick={() => setShowNew(true)} className="bg-[#E5276A] hover:bg-[#C01661]">
          <Plus className="w-4 h-4 mr-1" /> Nouvelle organisation
        </Button>
      </div>

      {showNew && (
        <Card className="mb-4"><CardContent className="p-4 flex gap-2">
          <Input data-testid="new-org-name" placeholder="Nom de l'organisation" value={name} onChange={(e) => setName(e.target.value)} />
          <Button data-testid="new-org-submit" onClick={createOrg} disabled={busy} className="bg-[#E5276A] hover:bg-[#C01661]">Créer</Button>
          <Button variant="ghost" onClick={() => setShowNew(false)}>Annuler</Button>
        </CardContent></Card>
      )}

      {/* ━━━ MES ÉVÉNEMENTS ━━━ */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-display text-[#1F3A5F] flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#E5276A]" /> Mes événements
          </h2>
          <span className="text-xs text-neutral-500">{events.length}</span>
        </div>
        {events.length === 0 ? (
          <Card><CardContent className="p-6 text-center text-sm text-neutral-500">
            Aucun événement pour l'instant. Créez une organisation puis un événement pour démarrer.
          </CardContent></Card>
        ) : (
          <div className="grid gap-2">
            {events.map(({ ev, org }) => (
              <Link key={ev.id} to={`/pro/experience/events/${ev.id}`} data-testid={`event-row-${ev.id}`}>
                <Card className="hover:border-[#E5276A] transition">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div
                        className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{
                          background: `${(ev.branding?.primary_color || '#E5276A')}15`,
                          color: ev.branding?.primary_color || '#E5276A',
                        }}
                      >
                        <Calendar className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-semibold text-[#1F3A5F] truncate">{ev.name}</span>
                          <StatusPill status={ev.status} />
                        </div>
                        <div className="text-xs text-neutral-500 flex items-center gap-3 flex-wrap">
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatParisDate(ev.starts_at)}
                          </span>
                          {ev.location?.address && (
                            <span className="inline-flex items-center gap-1 truncate">
                              <MapPin className="w-3 h-3" /> {ev.location.address}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-neutral-400 mt-1">Org : {org.name}</div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* ━━━ ORGANISATIONS ━━━ */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-display text-[#1F3A5F] flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#E5276A]" /> Vos organisations
          </h2>
          <span className="text-xs text-neutral-500">{orgs.length}</span>
        </div>
        <div className="grid gap-2">
          {orgs.length === 0 && (
            <Card><CardContent className="p-6 text-center text-sm text-neutral-500">
              Aucune organisation. Cliquez "Nouvelle organisation" pour commencer.
            </CardContent></Card>
          )}
          {orgs.map(o => (
            <Link key={o.id} to={`/pro/experience/orgs/${o.id}`} data-testid={`org-${o.id}`}>
              <Card className="hover:border-[#E5276A] transition">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#E5276A]/10 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-[#E5276A]" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-[#1F3A5F]">{o.name}</div>
                    <div className="text-xs text-neutral-500 flex items-center gap-2 mt-0.5">
                      {o.charges_enabled ? (
                        <span className="flex items-center gap-1 text-emerald-600"><CheckCircle2 className="w-3 h-3" /> Stripe actif</span>
                      ) : (
                        <span className="flex items-center gap-1 text-amber-600"><AlertCircle className="w-3 h-3" /> Stripe à activer</span>
                      )}
                      · commission {(o.default_commission_pct / 100).toFixed(1)}%
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-neutral-400" />
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    draft: { label: "Brouillon", cls: "bg-neutral-100 text-neutral-600" },
    published: { label: "Publié", cls: "bg-emerald-100 text-emerald-700" },
    live: { label: "En direct", cls: "bg-blue-100 text-blue-700" },
    closed: { label: "Terminé", cls: "bg-neutral-100 text-neutral-500" },
  };
  const s = map[status] || map.draft;
  return <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${s.cls}`}>{s.label}</span>;
}
